import React, {useEffect, useState} from 'react';
import {Layout} from '../components/common/Layout';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import classNames from './Stages.module.css'
import {AddEditModal} from '../components/stage/AddEditModal/AddEditModal';
import {useMutation, useQuery} from "@apollo/client";
import {GET_STAGES, ListStage, Stage} from "../api/stage/getStages";
import {StageOrderUpdateInput, UPDATE_STAGES_ORDER} from "../api/stage/updateStagesOrder";

export const Stages = () => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedStage, setSelectedStage] = useState<undefined | Stage>()
    const [stages, setStages] = useState<ListStage[]>([])

    const {loading, data, refetch} = useQuery(GET_STAGES);
    const [updateStagesOrder] = useMutation(UPDATE_STAGES_ORDER)


    const moveUp = async (stage: ListStage) => {
        if (!data) return

        const newOrderedStages: StageOrderUpdateInput[] = []

        stages.forEach(
            ({id, order}) => {
                if (id === stage.id) newOrderedStages.push({id, order: stage.order - 1})
                else if (order === stage.order - 1) return newOrderedStages.push({id, order: stage.order})
            }
        )

        await updateStagesOrder({
            variables: {
                stages: newOrderedStages
            }
        })

        refetch()
    }

    const moveDown = async (stage: ListStage) => {
        if (!data) return

        const newOrderedStages: StageOrderUpdateInput[] = []

        stages.forEach(
            ({id, order}) => {
                if (id === stage.id) newOrderedStages.push({id, order: stage.order + 1})
                else if (order === stage.order + 1) return newOrderedStages.push({id, order: stage.order})
            }
        )

        await updateStagesOrder({
            variables: {
                stages: newOrderedStages
            }
        })

        refetch()
    }

    useEffect(() => {
        if (data !== undefined) {
            const sortedStages = [...data.getAllStages].sort((a, b) => a.order - b.order)

            const formattedStages = sortedStages.map(stage => {
                if (stage.stageFiles && stage.type === 'UPLOAD') {
                    const files = stage.stageFiles.map(({title, requiredFileType}) => ({title, type: requiredFileType}))

                    return {...stage, files}
                }
                else return stage
            })

            setStages(formattedStages)
        }
    }, [data])

    return (
        <Layout title="Stages" isLoading={loading} showHeader showMenu>
            <Stack alignItems="flex-end" marginBottom="2rem">
                <Button variant="contained" color="success" onClick={() => setIsModalOpen(true)}>
                    Add New Stage
                </Button>
            </Stack>

            <TableContainer component={Paper}>
                <Table className={classNames.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Last updated By</TableCell>
                            <TableCell>Last updated At</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stages.map((row, index) => (
                            <TableRow key={row.id} className={classNames.row}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{row.type}</TableCell>
                                <TableCell>{row.title}</TableCell>
                                <TableCell>{row.lastUpdatedBy}</TableCell>
                                <TableCell>{row.updatedAt ? new Date(row.updatedAt).toUTCString() : ''}</TableCell>
                                <TableCell>
                                    <Stack direction="row" gap="0.5rem">
                                        <Button size="small" variant="contained" color="info" onClick={() => {
                                            setIsModalOpen(true)
                                            setSelectedStage(row)
                                        }}>
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="warning"
                                            className={classNames.iconButton}
                                            onClick={() => moveUp(row)}
                                            disabled={row.order === 1}
                                        >
                                            <ArrowUpwardIcon />
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="warning"
                                            className={classNames.iconButton}
                                            onClick={() => moveDown(row)}
                                            disabled={row.order === stages.length}
                                        >
                                            <ArrowDownwardIcon />
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {isModalOpen && (
                <AddEditModal
                    onClose={(shouldRefetch: boolean) => {
                        if (shouldRefetch) refetch()
                        setIsModalOpen(false)
                        setSelectedStage(undefined)
                    }}
                    stage={selectedStage}
                />
            )}
        </Layout>
    )
}
